import startCase from 'lodash/startCase';

import type {
  DataAuditEntityType,
  DataAuditStatusOption,
  DataAuditWritebackType,
} from 'src/components/data-audit/types';
import {
  DATA_AUDIT_NWB_SOURCE_VALUES,
  DATA_AUDIT_OWB_FWB_SOURCE_VALUES,
} from 'src/components/data-audit/types';

import type {
  DataAuditTranslate,
  LabelTranslationFn,
  StatusSemantic,
} from './label-types';


export type {DataAuditTranslate} from './label-types';

export function getWritebackAuditStatusLabel(
  status: string,
  writebackType: DataAuditWritebackType,
  t: LabelTranslationFn,
): string {
  if (writebackType === 'nwb') {
    switch (status) {
      case 'unsupported':
        return t('Unsupported', 'Unsupported');
      case 'held_for_batch':
        return t('Held_for_Batch', 'Held for Batch');
      case 'success':
        return t('Success', 'Success');
      case 'fail':
        return t('Failed', 'Failed');
      case 'batched':
        return t('Batched', 'Batched');
      case 'deleted':
        return t('Deleted', 'Deleted');
      case 'init':
        return t('Init', 'Init');
      case 'processing':
        return t('Processing', 'Processing');
      case 'skipped':
        return t('Skipped', 'Skipped');
      case 'ignored':
        return t('Ignored', 'Ignored');
      default:
        return startCase(status.replace(/_/g, ' '));
    }
  }

  switch (status) {
    case 'SUCCEEDED':
      return t('Succeeded', 'Succeeded');
    case 'SKIPPED':
      return t('Skipped', 'Skipped');
    case 'PARTIAL_FAILED':
      return t('Partial_Failed', 'Partial Failed');
    case 'FAILED':
      return t('Failed', 'Failed');
    case 'IN_PROGRESS':
      return t('In_Progress', 'In Progress');
    default:
      return startCase(status.replace(/_/g, ' '));
  }
}

export function getWritebackAuditStatusSemantic(
  status: string,
  writebackType: DataAuditWritebackType,
): StatusSemantic {
  if (writebackType === 'nwb') {
    const nwbSemanticMap: Record<string, StatusSemantic> = {
      success: 'success',
      fail: 'danger',
      processing: 'information',
      init: 'information',
      skipped: 'secondary',
      ignored: 'secondary',
      batched: 'information',
      held_for_batch: 'information',
      unsupported: 'danger',
      deleted: 'danger',
    };

    return nwbSemanticMap[status] ?? 'secondary';
  }

  const owbSemanticMap: Record<string, StatusSemantic> = {
    SUCCEEDED: 'success',
    FAILED: 'danger',
    PARTIAL_FAILED: 'danger',
    IN_PROGRESS: 'information',
    SKIPPED: 'warning',
  };

  return owbSemanticMap[status] ?? 'secondary';
}

export function mapWritebackAuditStatusesToOptions(
  statuses: Array<string>,
  writebackType: DataAuditWritebackType,
  t: LabelTranslationFn,
): Array<DataAuditStatusOption> {
  return statuses.map((status) => ({
    key: status,
    label: getWritebackAuditStatusLabel(status, writebackType, t),
  }));
}

function getDataAuditWritebackSourceValues(
  writebackType: DataAuditWritebackType,
): Array<string> {
  if (writebackType === 'nwb') {
    return [...DATA_AUDIT_NWB_SOURCE_VALUES];
  }

  return [...DATA_AUDIT_OWB_FWB_SOURCE_VALUES];
}

export function mapDataAuditWritebackSourcesToOptions(
  writebackType: DataAuditWritebackType,
  t: LabelTranslationFn,
): Array<DataAuditStatusOption> {
  return getDataAuditWritebackSourceValues(writebackType).map((source) => ({
    key: source,
    label: getDataAuditSourceLabel(source, t),
  }));
}

function getDataAuditSourceLabel(
  source: string,
  t: LabelTranslationFn,
): string {
  const normalized = source.toLowerCase();
  const fallback = startCase(source.replace(/_/g, ' ').toLowerCase());

  switch (normalized) {
    case 'meetings':
      return t('Meetings', fallback);
    case 'messaging':
      return t('Messaging', fallback);
    case 'engage':
      return t('Engage', fallback);
    case 'automation_workflow':
      return t('Automation_Workflow', fallback);
    case 'crm':
      return t('CRM', fallback);
    case 'chatbot':
      return t('Chatbot', fallback);
    case 'referral':
      return t('Referral', fallback);
    case 'job_matching':
      return t('Job_Matching', fallback);
    default:
      return fallback;
  }
}

export function formatDataAuditSourceLabel(
  source: string | null | undefined,
  writebackType: DataAuditWritebackType,
  t: LabelTranslationFn,
): string {
  if (!source) {
    return '-';
  }

  const normalizedSource = source.toLowerCase();
  const option = mapDataAuditWritebackSourcesToOptions(writebackType, t).find(
    (item) => item.key.toLowerCase() === normalizedSource,
  );

  return option?.label ?? getDataAuditSourceLabel(source, t);
}

export function mapDataAuditEntityTypesToAtsEntityOptions(
  entityTypes: Array<DataAuditEntityType> | undefined,
  {t, makeKey}: DataAuditTranslate,
): Array<DataAuditStatusOption> {
  return (entityTypes ?? [])
    .filter((entityType) => entityType.atsEntityType)
    .map((entityType) => ({
      key: entityType.atsEntityType,
      label: t(makeKey(entityType.atsEntityType), entityType.atsEntityType),
    }));
}

export function mapDataAuditNoteTypesToOptions(
  noteTypes: Array<string> | undefined,
  {t, makeKey}: DataAuditTranslate,
): Array<DataAuditStatusOption> {
  return (noteTypes ?? []).map((noteType) => {
    const fallback = startCase(noteType.replace(/_/g, ' '));

    return {
      key: noteType,
      label: t(makeKey(noteType), fallback),
    };
  });
}
